import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { PartnerPublicPremiumCard, PartnerPublicRegularCard } from '../shared/PartnerPublicCard';
import { PARTNER_PUBLIC_CARD_CSS } from '../shared/partnerPublicCardStyles';
import {
  TIER_ORDER,
  tierSlug,
  getTier,
  usesPremiumGridForTier,
  PUBLIC_API_BASE,
  normalizePartnersList,
} from '../../utils/partnerPublicDisplay';

/* ─── SVG Icons ─── */
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
  </svg>
);
const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" stroke="none"/>
  </svg>
);
const YoutubeIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22.5 6.5s-.3-2-1.2-2.8C20.2 2.5 18.9 2.5 18.3 2.4 15.6 2.2 12 2.2 12 2.2s-3.6 0-6.3.2C5.1 2.5 3.8 2.5 2.7 3.7 1.8 4.5 1.5 6.5 1.5 6.5S1.3 8.8 1.3 11v2c0 2.3.2 4.5.2 4.5s.3 2 1.2 2.8c1.1 1.2 2.6 1.1 3.3 1.2C8.2 21.7 12 21.7 12 21.7s3.6 0 6.3-.2c.6-.1 1.9-.1 3-1.3.9-.8 1.2-2.8 1.2-2.8s.2-2.3.2-4.5v-2C22.7 8.8 22.5 6.5 22.5 6.5z"/>
    <polygon points="10,8.5 15.5,12 10,15.5" fill="currentColor" stroke="none"/>
  </svg>
);
const LinkIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
  </svg>
);
const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
  </svg>
);
const MailIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <polyline points="2,4 12,13 22,4"/>
  </svg>
);
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.95 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012.86 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
  </svg>
);
const HandshakeIcon = () => (
  <svg viewBox="0 0 48 48" width="48" height="48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 28l8-8 6 4 8-8 14 8" stroke="#ff4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M10 32l4-4 6 4 8-8 10 6-4 4-6-4-8 8-10-6z" stroke="#ff4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="rgba(255,68,68,0.1)"/>
  </svg>
);
const RocketIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2C10 2 14 5 14 11L10 15 6 11C6 5 10 2 10 2z" fill="rgba(255,255,255,0.15)"/>
    <circle cx="10" cy="8" r="2"/><path d="M6 11L3 16 7 14.5M14 11L17 16 13 14.5"/>
  </svg>
);
const DownloadIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 15h14M10 3v9M6 8l4 4 4-4"/>
  </svg>
);
const WarningIcon = () => (
  <svg viewBox="0 0 48 48" width="64" height="64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 6L4 42h40L24 6z" stroke="#ff4444" strokeWidth="2" fill="rgba(255,68,68,0.1)" strokeLinejoin="round"/>
    <line x1="24" y1="20" x2="24" y2="30" stroke="#ff4444" strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="24" cy="36" r="1.5" fill="#ff4444"/>
  </svg>
);
const RetryIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 4v6h6M23 20v-6h-6"/>
    <path d="M20.49 9A9 9 0 005.64 5.64L1 10m18 0l-4.64 4.36A9 9 0 013.51 11"/>
  </svg>
);

/* ─── Particle Canvas ─── */
const ParticleCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    let raf;
    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; };
    resize(); window.addEventListener('resize', resize);
    const pts = Array.from({ length: 50 }, () => ({
      x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.32, vy: (Math.random() - 0.5) * 0.32,
      r: Math.random() * 1.4 + 0.4, a: Math.random() * 0.4 + 0.08,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = c.width; if (p.x > c.width) p.x = 0;
        if (p.y < 0) p.y = c.height; if (p.y > c.height) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,68,68,${p.a})`; ctx.fill();
      });
      for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y, d = Math.sqrt(dx*dx+dy*dy);
        if (d < 120) {
          ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y);
          ctx.strokeStyle = `rgba(255,68,68,${0.06*(1-d/120)})`; ctx.lineWidth=0.5; ctx.stroke();
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} style={{position:'fixed',inset:0,zIndex:0,pointerEvents:'none'}} />;
};

/* ─── useInView (callback ref so observe runs after the DOM node exists) ─── */
const useInView = (threshold = 0.1) => {
  const [element, setElement] = useState(null);
  const [inView, setInView] = useState(false);
  const setRef = useCallback((node) => {
    setElement(node);
  }, []);
  useEffect(() => {
    if (!element) return undefined;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) setInView(true);
      },
      { threshold, rootMargin: '100px 0px 0px 0px' }
    );
    obs.observe(element);
    return () => obs.disconnect();
  }, [element, threshold]);
  return [setRef, inView];
};

/* ─── Animated Value ─── */
const AnimatedValue = ({ value, prefix = '', inView }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const target = parseFloat(value) || 0;
    const duration = 1600;
    const steps = 60;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      setDisplay(Math.round(target * (step / steps)));
      if (step >= steps) clearInterval(timer);
    }, duration / steps);
    return () => clearInterval(timer);
  }, [inView, value]);
  return <>{prefix}{display.toLocaleString()}</>;
};

/* ─── Section Header ─── */
const SectionHeader = ({ tag, title, subtitle, inView }) => (
  <div className="sh">
    <span className="sh-tag">{tag}</span>
    <h2 className={`sh-title ${inView ? 'underline-in' : ''}`}>{title}</h2>
    <div className="sh-bar" />
    {subtitle && <p className="sh-sub">{subtitle}</p>}
  </div>
);

/* ─── Main Component ─── */
const Partner = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [heroVisible, setHeroVisible] = useState(false);

  const [premRef, premInView] = useInView();
  const [suppRef, suppInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  useEffect(() => {
    setTimeout(() => setHeroVisible(true), 120);
    fetchPublicPartners();
  }, []);

  const fetchPublicPartners = async () => {
    try {
      setLoading(true); setError('');
      const response = await axios.get(`${PUBLIC_API_BASE}/partners/public`);
      setPartners(normalizePartnersList(response.data));
    } catch (err) {
      if (err.response) setError(`Server error ${err.response.status} — please try again later.`);
      else if (err.request) setError('Unable to connect. Check your internet connection.');
      else setError('Failed to load partners. Please try again.');
    } finally { setLoading(false); }
  };

  const partnersByTier = TIER_ORDER.map((tierKey) => {
    const list = partners.filter((p) => tierSlug(p) === tierKey);
    return { tierKey, list };
  }).filter((x) => x.list.length > 0);

  /* Loading */
  if (loading) return (
    <div className="pg ext-page">
      <ParticleCanvas />
      <div className="grid-bg" /><div className="scanlines" />
      <div className="state-wrap">
        <div className="loading-rig">
          <div className="lr-ring lr-ring-1" />
          <div className="lr-ring lr-ring-2" />
          <div className="lr-ring lr-ring-3" />
          <div className="lr-core" />
        </div>
        <div className="load-tag">// FETCHING_PARTNERS</div>
        <h2 className="load-title">LOADING PARTNERS</h2>
        <p className="load-sub">Please wait while we retrieve partner data…</p>
      </div>
      <Styles />
    </div>
  );

  /* Error */
  if (error) return (
    <div className="pg ext-page">
      <ParticleCanvas />
      <div className="grid-bg" /><div className="scanlines" />
      <div className="state-wrap">
        <div className="err-icon"><WarningIcon /></div>
        <div className="err-tag">// ERROR_STATE</div>
        <h2 className="err-title">CONNECTION FAILED</h2>
        <p className="err-msg">{error}</p>
        <button className="retry-btn" onClick={fetchPublicPartners}>
          <span className="retry-shine" />
          <span className="retry-inner"><RetryIcon /> RETRY CONNECTION</span>
        </button>
      </div>
      <Styles />
    </div>
  );

  return (
    <div className="pg ext-page">
      <ParticleCanvas />
      <div className="grid-bg" aria-hidden="true" />
      <div className="scanlines" aria-hidden="true" />
      <div className="radial-glow" aria-hidden="true" />

      <div className="wrapper">

        {/* ── HERO ── */}
        <section className={`hero ${heroVisible ? 'vis' : ''}`}>
          <div className="hero-tag">
            <span className="ht-dot" />
            <span>UNIVERSITI TEKNOLOGI MALAYSIA // PARTNERS_MODULE</span>
          </div>
          <h1 className="hero-title">
            OUR<br />
            <span className="hero-accent">PART<span className="hero-red">NERS</span></span>
          </h1>
          <div className="hero-divider">
            <div className="hd-l"/><span className="hd-text">VERIFIED_PARTNERSHIPS</span><div className="hd-r"/>
          </div>
          <p className="hero-desc">
            Proudly collaborating with organizations that fuel our robotics journey and champion innovation excellence.
          </p>
          <div className="hero-sweep-line"><div className="hsl-inner"/></div>
        </section>

        {/* ── TIERED PARTNERS ── */}
        {partnersByTier.length > 0 && (
          <section className={`sec ${suppInView ? 'sec-in' : ''}`} ref={suppRef}>
            <div className="sec-shell">
              <SectionHeader
                tag="// TIERED_PARTNERS"
                title="PARTNERS BY SPONSOR TIER"
                subtitle="Diamond, Platinum, Gold, Silver and Bronze partners grouped by tier priority"
                inView={suppInView}
              />
              {partnersByTier.map(({ tierKey, list }) => {
                const tierMeta = getTier(tierKey);
                const usePremiumGrid = usesPremiumGridForTier(tierKey);
                return (
                  <div key={tierKey} className="tier-section">
                    <h3 className={`tier-title tier-title-${tierKey}`} style={{ '--tier-color': tierMeta.color }}>
                      {tierKey.toUpperCase()} PARTNERS
                    </h3>
                    <div className={usePremiumGrid ? 'premium-grid' : 'regular-grid'}>
                      {list.map((p, i) => (
                        usePremiumGrid
                          ? <PartnerPublicPremiumCard key={p.id} partner={p} index={i} />
                          : <PartnerPublicRegularCard key={p.id} partner={p} index={i} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── CTA ── */}
        <section className={`sec cta-sec ${ctaInView ? 'sec-in' : ''}`} ref={ctaRef}>
          <div className="sec-shell">
            <div className="glass-card cta-card">
              <div className="cta-sweep" />
              <div className="card-top-line" />
              <div className="cta-alert"><span className="ca-dot"/><span>// PARTNERSHIPS_OPEN</span></div>
              <h2 className="cta-title">PARTNER WITH CHAMPIONS</h2>
              <p className="cta-desc">Join our prestigious network and be part of Malaysia's robotics excellence. Together, we'll innovate and inspire the next generation of engineers.</p>
              <div className="cta-btns">
                <button className="btn-primary">
                  <span className="btn-shine"/>
                  <span className="btn-inner"><RocketIcon/> BECOME A PARTNER</span>
                </button>
                <button className="btn-secondary">
                  <DownloadIcon/> DOWNLOAD PROPOSAL
                </button>
              </div>
              <div className="card-bottom-line" />
            </div>
          </div>
        </section>

      </div>
      <Styles />
    </div>
  );
};

/* ─── Styles Component ─── */
const Styles = () => (
  <style>{`
    ${PARTNER_PUBLIC_CARD_CSS}
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

    .pg {
      position:relative;min-height:100vh;background:#060608;
      font-family:'Rajdhani',-apple-system,BlinkMacSystemFont,sans-serif;
      color:#e0e0e0;overflow-x:hidden;
    }

    /* Overlays */
    .grid-bg{position:fixed;inset:0;z-index:1;pointer-events:none;
      background-image:linear-gradient(rgba(255,68,68,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,68,68,0.03) 1px,transparent 1px);
      background-size:72px 72px;}
    .scanlines{position:fixed;inset:0;z-index:2;pointer-events:none;
      background:repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.05) 2px,rgba(0,0,0,0.05) 4px);}
    .radial-glow{position:fixed;inset:0;z-index:1;pointer-events:none;
      background:radial-gradient(ellipse at 20% 80%,rgba(255,34,34,0.06),transparent 55%),
                 radial-gradient(ellipse at 80% 20%,rgba(255,34,34,0.04),transparent 55%);}

    /* Shared wrapper/section/header styles are in external.css */

    /* ── Hero ── */
    .hero{padding:6.5rem 0 4.5rem;text-align:center;opacity:0;transform:translateY(26px);transition:opacity 0.9s ease,transform 0.9s ease;}
    .hero.vis{opacity:1;transform:none;}
    .hero-tag{display:inline-flex;align-items:center;gap:8px;background:rgba(255,68,68,0.07);border:1px solid rgba(255,68,68,0.2);border-radius:2px;padding:5px 14px;margin-bottom:1.8rem;font-family:'Share Tech Mono',monospace;font-size:0.62rem;color:#ff7766;letter-spacing:1.5px;}
    .ht-dot{width:7px;height:7px;border-radius:50%;background:#ff4444;box-shadow:0 0 8px #ff4444;animation:blink 1.6s ease-in-out infinite;}
    @keyframes blink{0%,100%{opacity:1;box-shadow:0 0 8px #ff4444}50%{opacity:0.3;box-shadow:0 0 3px #ff4444}}
    .hero-title{font-family:'Orbitron',sans-serif;font-size:clamp(2.8rem,7vw,5.5rem);font-weight:900;color:#fff;line-height:0.9;margin-bottom:1.6rem;}
    .hero-accent{display:block;}
    .hero-red{color:#ff2222;text-shadow:0 0 18px rgba(255,34,34,0.7),0 0 40px rgba(255,34,34,0.35);animation:glow-pulse 3s ease-in-out infinite;}
    @keyframes glow-pulse{0%,100%{text-shadow:0 0 18px rgba(255,34,34,0.7),0 0 40px rgba(255,34,34,0.35)}50%{text-shadow:0 0 28px rgba(255,34,34,0.95),0 0 60px rgba(255,34,34,0.55)}}
    .hero-divider{display:flex;align-items:center;gap:1rem;margin:0 auto 1.5rem;max-width:460px;}
    .hd-l{flex:1;height:1px;background:linear-gradient(90deg,transparent,#ff4444);}
    .hd-r{flex:1;height:1px;background:linear-gradient(90deg,#ff4444,transparent);}
    .hd-text{font-family:'Share Tech Mono',monospace;font-size:0.6rem;color:#ff6644;letter-spacing:3px;white-space:nowrap;}
    .hero-desc{font-size:1.1rem;color:#8a97a8;line-height:1.75;max-width:560px;margin:0 auto 1.6rem;}
    .hero-sweep-line{width:120px;height:2px;background:rgba(255,68,68,0.18);border-radius:1px;overflow:hidden;position:relative;margin:0 auto;}
    .hsl-inner{position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg,transparent,#ff4444,transparent);animation:sweep-ln 2.5s ease-in-out infinite;}
    @keyframes sweep-ln{0%{left:-100%}100%{left:200%}}

    /* ── Glass base ── */
    .glass-card{background:rgba(8,8,14,0.86);border:1px solid rgba(255,68,68,0.12);border-radius:5px;position:relative;overflow:hidden;backdrop-filter:blur(18px);}
    .card-top-line{position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(255,68,68,0.65),transparent);pointer-events:none;}
    .card-bottom-line{position:absolute;bottom:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(255,68,68,0.2),transparent);pointer-events:none;}

    .tier-section{margin-bottom:2.5rem;}
    .tier-section:last-child{margin-bottom:0;}
    .tier-title{
      font-family:'Orbitron',sans-serif;
      letter-spacing:3px;
      margin:0 0 1rem;
      color:var(--tier-color);
      text-shadow:0 0 14px color-mix(in srgb, var(--tier-color) 35%, transparent);
    }
    .tier-title-diamond{font-size:clamp(1.8rem,4vw,2.5rem);font-weight:900;}
    .tier-title-platinum{font-size:clamp(1.45rem,3.2vw,2rem);font-weight:800;}
    .tier-title-gold{font-size:clamp(1.2rem,2.8vw,1.6rem);font-weight:800;}
    .tier-title-silver,.tier-title-bronze{font-size:clamp(1rem,2.3vw,1.3rem);font-weight:700;}

    /* ── Empty ── */
    .empty-sec{text-align:center;}
    .empty-card{padding:4rem 3rem;width:100%;transition:border-color 0.3s,box-shadow 0.3s;}
    .empty-card:hover{border-color:rgba(255,68,68,0.3);box-shadow:0 0 28px rgba(255,68,68,0.08);}
    .empty-icon-wrap{width:80px;height:80px;border-radius:50%;background:rgba(255,68,68,0.06);border:1px solid rgba(255,68,68,0.18);display:flex;align-items:center;justify-content:center;margin:0 auto 1.4rem;}
    .empty-tag{font-family:'Share Tech Mono',monospace;font-size:0.6rem;color:#555;letter-spacing:1.5px;margin-bottom:0.6rem;}
    .empty-title{font-family:'Orbitron',sans-serif;font-size:1.2rem;font-weight:700;color:#fff;letter-spacing:3px;margin-bottom:1rem;}
    .empty-desc{font-size:0.95rem;color:#7a8a9a;line-height:1.7;margin-bottom:2rem;}
    .empty-btns{display:flex;gap:1rem;justify-content:center;flex-wrap:wrap;}

    /* ── CTA ── */
    .cta-sec{}
    .cta-card{padding:3.5rem;width:100%;text-align:center;transition:border-color 0.3s,box-shadow 0.3s;}
    .cta-card:hover{border-color:rgba(255,68,68,0.3);box-shadow:0 0 32px rgba(255,68,68,0.1);}
    .cta-sweep{position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse at 50% 50%,rgba(255,34,34,0.07),transparent 70%);animation:cta-sw 6s ease-in-out infinite alternate;}
    @keyframes cta-sw{from{transform:scaleX(0.85)}to{transform:scaleX(1.1)}}
    .cta-alert{display:inline-flex;align-items:center;gap:8px;font-family:'Share Tech Mono',monospace;font-size:0.6rem;color:#ff6644;letter-spacing:2px;margin-bottom:1.2rem;}
    .ca-dot{width:6px;height:6px;border-radius:50%;background:#ff4444;box-shadow:0 0 6px #ff4444;animation:blink 1s infinite;}
    .cta-title{font-family:'Orbitron',sans-serif;font-size:clamp(1.4rem,3vw,2rem);font-weight:900;color:#fff;letter-spacing:3px;margin-bottom:1rem;}
    .cta-desc{font-size:1rem;color:#7a8a9a;line-height:1.7;margin-bottom:2rem;max-width:580px;margin-left:auto;margin-right:auto;}
    .cta-btns{display:flex;gap:1.2rem;justify-content:center;flex-wrap:wrap;}

    /* ── Buttons ── */
    .btn-primary{position:relative;overflow:hidden;background:#cc1111;border:none;border-radius:3px;cursor:pointer;padding:0;font-family:'Orbitron',sans-serif;box-shadow:0 0 16px rgba(255,17,17,0.3);animation:btn-pulse 2.5s ease-in-out infinite;transition:transform 0.25s,box-shadow 0.25s;}
    @keyframes btn-pulse{0%,100%{box-shadow:0 0 14px rgba(255,34,34,0.3)}50%{box-shadow:0 0 28px rgba(255,34,34,0.6)}}
    .btn-primary:hover{transform:translateY(-2px) scale(1.03);box-shadow:0 0 32px rgba(255,34,34,0.55);}
    .btn-shine{position:absolute;top:0;left:-80%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent);transition:left 0.5s ease;}
    .btn-primary:hover .btn-shine{left:130%;}
    .btn-inner{position:relative;z-index:1;display:flex;align-items:center;gap:8px;padding:0.9rem 2rem;font-size:0.7rem;font-weight:700;color:#fff;letter-spacing:2px;}
    .btn-secondary{background:transparent;color:#ff4444;border:1px solid rgba(255,68,68,0.4);border-radius:3px;padding:0.9rem 2rem;cursor:pointer;font-family:'Orbitron',sans-serif;font-size:0.7rem;font-weight:600;letter-spacing:2px;display:flex;align-items:center;gap:8px;transition:all 0.25s;}
    .btn-secondary:hover{background:rgba(255,68,68,0.1);border-color:#ff4444;box-shadow:0 0 18px rgba(255,68,68,0.2);transform:translateY(-2px);}

    /* ── Loading / Error states ── */
    .state-wrap{position:relative;z-index:3;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:2rem;text-align:center;}
    .loading-rig{position:relative;width:90px;height:90px;margin:0 auto 2.5rem;}
    .lr-ring{position:absolute;border-radius:50%;border-style:solid;border-color:transparent;}
    .lr-ring-1{inset:0;border-width:2px;border-top-color:#ff4444;animation:spin 1.2s linear infinite;box-shadow:0 0 12px rgba(255,68,68,0.4);}
    .lr-ring-2{inset:10px;border-width:2px;border-right-color:rgba(255,68,68,0.6);animation:spin 2s linear infinite reverse;}
    .lr-ring-3{inset:20px;border-width:1px;border-bottom-color:rgba(255,68,68,0.35);animation:spin 3s linear infinite;}
    @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
    .lr-core{position:absolute;inset:36px;border-radius:50%;background:#ff4444;box-shadow:0 0 14px #ff4444;animation:blink 1.2s ease-in-out infinite;}
    .load-tag{font-family:'Share Tech Mono',monospace;font-size:0.62rem;color:#555;letter-spacing:2px;margin-bottom:0.8rem;}
    .load-title{font-family:'Orbitron',sans-serif;font-size:1.4rem;font-weight:700;color:#fff;letter-spacing:4px;margin-bottom:0.6rem;}
    .load-sub{font-size:0.9rem;color:#7a8a9a;}

    .err-icon{margin:0 auto 1.5rem;animation:warn-pulse 2s ease-in-out infinite;}
    @keyframes warn-pulse{0%,100%{filter:drop-shadow(0 0 8px rgba(255,68,68,0.4))}50%{filter:drop-shadow(0 0 18px rgba(255,68,68,0.7))}}
    .err-tag{font-family:'Share Tech Mono',monospace;font-size:0.62rem;color:#555;letter-spacing:2px;margin-bottom:0.8rem;}
    .err-title{font-family:'Orbitron',sans-serif;font-size:1.4rem;font-weight:700;color:#fff;letter-spacing:4px;margin-bottom:0.8rem;}
    .err-msg{font-size:0.95rem;color:#7a8a9a;margin-bottom:2rem;max-width:440px;line-height:1.6;}
    .retry-btn{position:relative;overflow:hidden;background:#cc1111;border:none;border-radius:3px;cursor:pointer;padding:0;font-family:'Orbitron',sans-serif;animation:btn-pulse 2.5s ease-in-out infinite;}
    .retry-shine{position:absolute;top:0;left:-80%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent);transition:left 0.5s ease;}
    .retry-btn:hover .retry-shine{left:130%;}
    .retry-inner{position:relative;z-index:1;display:flex;align-items:center;gap:8px;padding:0.9rem 2rem;font-size:0.7rem;font-weight:700;color:#fff;letter-spacing:2px;}
    .retry-btn:hover{transform:translateY(-2px);box-shadow:0 0 28px rgba(255,34,34,0.5);}

    /* ── Responsive ── */
    @media(max-width:1024px){.premium-grid{grid-template-columns:1fr;}}
    @media(max-width:768px){
      .hero{padding:5rem 0 3rem;}
      .premium-grid{grid-template-columns:1fr;gap:1.8rem;}
      .regular-grid{grid-template-columns:1fr;}
      .cta-card{padding:2.5rem 1.8rem;}
      .empty-btns,.cta-btns{flex-direction:column;align-items:center;}
      .btn-primary,.btn-secondary{width:100%;justify-content:center;}
    }
    @media(max-width:480px){
      .pc-header{flex-direction:column;text-align:center;}
      .pc-badge{position:static;align-self:flex-end;margin-bottom:0.8rem;}
      .empty-card{padding:3rem 1.5rem;}
    }
  `}</style>
);

export default Partner;